@if (!Loaded) {
<div class="loading">Loading...</div>
} @else {

<!-- ================================================================
     Header: swap number + status + actions
     ================================================================ -->
<div class="detail-header">
  <div class="header-left">
    <h3>{{ isNew ? 'New Swap Request' : Swap?.SwapNumber }}</h3>
    @if (!isNew) {
    <span class="type-chip type-{{ SwapTypeID }}">{{ typeLabel }}</span>
    <span class="status-pill {{ statusClass() }}">{{ Swap?.StatusName }}</span>
    }
    @if (Swap?.CreatedOrderID) {
    <span class="order-link" (click)="openDraftOrder()">Draft Order #{{ Swap.CreatedOrderID }} ↗</span>
    }
    @if (Swap?.TargetReserveOrderID && statusId === 6) {
    <span class="muted small">→ Reserve {{ Swap.TargetReserveOrderID }}</span>
    }
  </div>
  <div class="header-actions">
    @if (canCancel) {
    <button class="btn-bg-red" [disabled]="Busy" (click)="cancelSwap()">Cancel Swap</button>
    }
    <button class="btn-bg-blue" (click)="returnToList()">← Return</button>
  </div>
</div>

<!-- Summary strip: who needs what, from whom -->
@if (!isNew) {
<div class="summary-strip">
  @if (isRelease) {
  <div><span class="muted">Release to</span><strong>General Population</strong></div>
  <div><span class="muted">Bags</span><strong>{{ Swap?.ProposedBags ?? Swap?.BagsRequested }} bags · {{ Swap?.SourceCoffee ?? Swap?.RequestedCoffee }}</strong></div>
  } @else {
  <div><span class="muted">{{ SwapTypeID === 1 ? 'Receiving reserve' : 'Requesting' }}</span><strong>{{ Swap?.RequestingCustomer }}</strong></div>
  <div><span class="muted">Needs</span><strong>{{ Swap?.BagsRequested }} bags · {{ Swap?.RequestedCoffee }}</strong></div>
  }
  <div><span class="muted">Source</span><strong>{{ Swap?.SourceCustomer ?? '—' }}{{ Swap?.SourceReserveOrderID ? ' · Reserve ' + Swap.SourceReserveOrderID : '' }}</strong></div>
  <div><span class="muted">Approver</span><strong>{{ Swap?.ApprovingTraderName ?? '—' }}</strong></div>
  <div><span class="muted">Requested by</span><strong>{{ Swap?.RequestedByName ?? '—' }}</strong></div>
</div>
}

@if (Swap?.SwapStatusID === 3 && Swap?.DecisionComments) {
<div class="banner banner-warning">
  Returned for changes by {{ Swap?.DecisionByName ?? 'trader' }}: {{ Swap.DecisionComments }}
</div>
}
@if (Swap?.SwapStatusID === 5) {
<div class="banner banner-error">Rejected by {{ Swap?.DecisionByName ?? 'trader' }}: {{ Swap?.DecisionComments }}</div>
}
@if (Swap?.SwapStatusID === 7) {
<div class="banner banner-error">Cancelled{{ Swap?.CancelReason ? ': ' + Swap.CancelReason : '' }}</div>
}

<rms-tab-group>

  <!-- ================================================================
       TAB 0 — Request & Source (6.2.2–6.2.17)
       ================================================================ -->
  <rms-tab label="Request & Source">
    <ng-template>

      <!-- Swap type — locked once submitted -->
      <div class="section">
        <h4>Swap Type</h4>
        <div class="type-picker">
          @for (t of SwapTypes; track t.id) {
          <label class="type-option" [class.active]="SwapTypeID === t.id" [class.disabled]="!canEdit">
            <input type="radio" name="swapType" [value]="t.id" [checked]="SwapTypeID === t.id"
                   [disabled]="!canEdit" (change)="onTypeChanged(t.id)" />
            {{ t.label }}
          </label>
          }
        </div>
        <p class="muted small type-hint">{{ typeHint }}</p>
      </div>

      @if (isRelease) {
      <!-- ============ RELEASE (type 3): pick a customer's own reserve line ============ -->
      <div class="section">
        <h4>Release From</h4>
        <div class="field-grid">
          <div class="field-group span-2">
            <rms-search-select label="Customer (whose reserve)"
                               [options]="Customers"
                               labelKey="LabelName"
                               valueKey="CompanyID"
                               [disabled]="!canEdit"
                               [ngModel]="ReleaseCompanyID"
                               (ngModelChange)="onReleaseCustomerChanged($event)">
            </rms-search-select>
          </div>

          <div class="field-group span-2">
            <rms-search-select label="Coffee (optional filter)"
                               [options]="CoffeeOptions"
                               labelKey="LabelName"
                               valueKey="InventoryTypeID"
                               [disabled]="!canEdit"
                               [(ngModel)]="ReleaseCoffeeFilter">
            </rms-search-select>
          </div>

          <div class="field-group">
            <rms-date label="Target Date" [(ngModel)]="TargetDate" [disabled]="!canEdit"></rms-date>
          </div>

          <div class="field-group">
            <label>Priority</label>
            <select [(ngModel)]="Priority" [disabled]="!canEdit">
              @for (p of PriorityOptions; track p) {
              <option [value]="p">{{ p }}</option>
              }
            </select>
          </div>

          <div class="field-group span-4">
            <label>Reason / Notes for approver</label>
            <textarea [(ngModel)]="Reason" [disabled]="!canEdit" maxlength="1000"
                      placeholder="e.g. Customer no longer needs these bags, contract reduced"></textarea>
          </div>
        </div>

        @if (canEdit) {
        <div class="button-row">
          <button class="btn-bg-blue" [disabled]="LoadingLines || Busy || !ReleaseCompanyID" (click)="loadReserveLines()">
            {{ LoadingLines ? 'Loading…' : 'Show Reserve Lines' }}
          </button>
        </div>
        }
      </div>

      } @else {
      <!-- ============ TYPES 1 / 2: request + ranked source search ============ -->
      <div class="section">
        <h4>Request</h4>
        <div class="field-grid">
          <div class="field-group span-2">
            <rms-search-select [label]="SwapTypeID === 1 ? 'Receiving Customer' : 'Requesting Customer'"
                               [options]="Customers"
                               labelKey="LabelName"
                               valueKey="CompanyID"
                               [disabled]="!canEdit"
                               [ngModel]="RequestingCompanyID"
                               (ngModelChange)="RequestingCompanyID = $event; onRequestKeyChanged()">
            </rms-search-select>
          </div>

          <div class="field-group span-2">
            <rms-search-select label="Coffee Needed"
                               [options]="CoffeeOptions"
                               labelKey="LabelName"
                               valueKey="InventoryTypeID"
                               [disabled]="!canEdit"
                               [ngModel]="RequestedInventoryTypeID"
                               (ngModelChange)="RequestedInventoryTypeID = $event; onRequestKeyChanged()">
            </rms-search-select>
          </div>

          <div class="field-group">
            <label>Bags Needed</label>
            <input type="number" min="1" [(ngModel)]="BagsRequested" [disabled]="!canEdit" />
          </div>

          <div class="field-group">
            <label>Destination Warehouse</label>
            <select [(ngModel)]="DestinationWarehouseID" [disabled]="!canEdit">
              <option [ngValue]="null">Any</option>
              @for (w of Warehouses; track w.LocationID) {
              <option [ngValue]="w.LocationID">{{ w.LocationName }}</option>
              }
            </select>
          </div>

          <div class="field-group">
            <rms-date label="Target Date" [(ngModel)]="TargetDate" [disabled]="!canEdit"></rms-date>
          </div>

          <div class="field-group">
            <label>Priority</label>
            <select [(ngModel)]="Priority" [disabled]="!canEdit">
              @for (p of PriorityOptions; track p) {
              <option [value]="p">{{ p }}</option>
              }
            </select>
          </div>

          <div class="field-group span-4">
            <label>Reason / Notes for approver</label>
            <textarea [(ngModel)]="Reason" [disabled]="!canEdit" maxlength="1000"
                      placeholder="e.g. Customer out of stock, next container delayed 3 weeks"></textarea>
          </div>

          <div class="field-group span-4 checkbox-row">
            <label><input type="checkbox" [(ngModel)]="CrossWarehouseAllowed" [disabled]="!canEdit" /> Allow sources from other warehouses</label>
            <label><input type="checkbox" [(ngModel)]="IncludeAlternatives" [disabled]="!canEdit" /> Include similar coffees (same origin)</label>
          </div>
        </div>

        @if (canEdit) {
        <div class="button-row">
          <button class="btn-bg-blue" [disabled]="Searching || Busy" (click)="findSources()">
            {{ Searching ? 'Searching…' : 'Find Sources' }}
          </button>
        </div>
        }
      </div>
      }

      <!-- Chosen source (6.2.17) -->
      @if (SelectedSource) {
      <div class="section selected-source">
        <h4>{{ isRelease ? 'Line to Release' : 'Selected Source' }}</h4>
        <div class="source-grid">
          <div><span class="muted">Customer</span><strong>{{ SelectedSource.SourceCustomer }}</strong></div>
          <div><span class="muted">Reserve / Lot</span><strong>{{ SelectedSource.ReserveOrderID }} / {{ SelectedSource.InventoryID }}</strong></div>
          <div><span class="muted">Coffee</span><strong>{{ SelectedSource.InventoryTypeName }}</strong></div>
          <div><span class="muted">Warehouse</span><strong>{{ SelectedSource.WarehouseName ?? '—' }}</strong></div>
          <div><span class="muted">Available</span><strong>{{ SelectedSource.AvailableToSwap }} bags</strong></div>
          <div><span class="muted">Price</span><strong>{{ SelectedSource.Price | number:'1.2-4' }} ({{ SelectedSource.PriceStatus }})</strong></div>
          @if (isRelease) {
          <div><span class="muted">Approver</span><strong>{{ SelectedSource.ApprovingTraderName ?? '—' }}</strong></div>
          } @else {
          <div>
            <span class="muted">Match</span>
            <strong>
              <span class="band {{ bandClass(SelectedSource.MatchScore) }}">{{ SelectedSource.MatchType }} · {{ SelectedSource.MatchScore }}</span>
            </strong>
          </div>
          }
          <div class="field-group">
            <label>{{ isRelease ? 'Bags to release' : 'Bags to transfer' }}</label>
            <input type="number" min="1" [max]="SelectedSource.AvailableToSwap"
                   [(ngModel)]="ProposedBags" [disabled]="!canEdit" />
          </div>
        </div>

        @if (canEdit) {
        <div class="button-row">
          <button class="btn-bg-red" [disabled]="Busy" (click)="clearSource()">{{ isRelease ? 'Change Line' : 'Remove Source' }}</button>
          <button class="btn-bg-blue" [disabled]="Busy" (click)="saveDraft()">Save Draft</button>
          <button class="btn-bg-green" [disabled]="Busy || !canSubmit" (click)="submitForApproval()">Submit for Approval</button>
        </div>
        }
      </div>
      } @else if (canEdit && !isRelease) {
      <div class="button-row">
        <button class="btn-bg-blue" [disabled]="Busy" (click)="saveDraft()">Save Draft (no source yet)</button>
      </div>
      }

      <!-- Release: the customer's open reserve lines -->
      @if (isRelease && LinesLoaded) {
      <div class="section">
        <h4>Open Reserve Lines</h4>
        @if (ReserveLines.length === 0) {
        <p class="muted">This customer has no open reserve bags{{ ReleaseCoffeeFilter ? ' for that coffee' : '' }}.</p>
        } @else {
        <div class="table-scroll">
          <table class="doc-table">
            <thead>
              <tr>
                <th>Reserve / Lot</th>
                <th>Coffee</th>
                <th>Whse</th>
                <th class="num">Reserved</th>
                <th class="num">On other swaps</th>
                <th class="num">Available</th>
                <th class="num">Price</th>
                <th>Contract</th>
                <th>Approver</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (l of ReserveLines; track l.ReserveOrderID + '-' + l.InventoryID) {
              <tr [class.selected-row]="isSelected(l)">
                <td>{{ l.ReserveOrderID }} / {{ l.InventoryID }}</td>
                <td>{{ l.InventoryTypeName }}</td>
                <td>{{ l.WarehouseName ?? '—' }}</td>
                <td class="num">{{ l.RemainingReservedBags }}</td>
                <td class="num">{{ l.PendingBags || '' }}</td>
                <td class="num"><strong>{{ l.AvailableToSwap }}</strong></td>
                <td class="num">
                  {{ l.Price | number:'1.2-4' }}
                  <div class="muted small">{{ l.PriceStatus }}</div>
                </td>
                <td class="nowrap">{{ l.ContractStartDate | date:'MM/dd/yyyy':'UTC' }} – {{ l.ContractEndDate | date:'MM/dd/yyyy':'UTC' }}</td>
                <td>{{ l.ApprovingTraderName ?? '—' }}</td>
                <td>
                  @if (canEdit) {
                  <button [disabled]="isSelected(l) || l.AvailableToSwap <= 0" (click)="selectCandidate(l)">
                    {{ isSelected(l) ? 'Selected' : 'Select' }}
                  </button>
                  }
                </td>
              </tr>
              }
            </tbody>
          </table>
        </div>
        }
      </div>
      }

      <!-- Candidate results (6.2.6–6.2.16) -->
      @if (Searched && !isRelease) {
      <div class="section">
        <h4>Available Sources</h4>

        @if (CandidateSummary) {
        <div class="chip-row">
          <span class="chip">{{ CandidateSummary.TotalCandidates }} lines · {{ CandidateSummary.TotalCustomers }} customers</span>
          <span class="chip">{{ CandidateSummary.TotalAvailableBags }} bags available</span>
          <span class="chip">{{ CandidateSummary.ExactMatches }} exact · {{ CandidateSummary.Alternatives }} similar</span>
          <span class="chip band-excellent">Excellent {{ CandidateSummary.Excellent }}</span>
          <span class="chip band-good">Good {{ CandidateSummary.Good }}</span>
          <span class="chip band-fair">Fair {{ CandidateSummary.Fair }}</span>
          <span class="chip">{{ CandidateSummary.SameWarehouse }} same whse · {{ CandidateSummary.CrossWarehouse }} other</span>
        </div>
        <p class="muted small">
          Usage (burn) is based on orders through {{ CandidateSummary.BurnDataAsOf | date:'MMM yyyy':'UTC' }}.
          @if (CandidateSummary.RequestedFlavorNotes) {
          Requested coffee's flavor notes: <em>{{ CandidateSummary.RequestedFlavorNotes }}</em>
          }
        </p>
        }

        @if (Candidates.length === 0) {
        <p class="muted">No other customer has open reserve bags that match. Try allowing other warehouses or similar coffees.</p>
        } @else {
        <div class="table-scroll">
          <table class="doc-table candidate-table">
            <thead>
              <tr>
                <th>Match</th>
                <th>Source Customer</th>
                <th>Coffee</th>
                <th>Whse</th>
                <th class="num">Available</th>
                <th class="num">Burn / mo</th>
                <th class="num">Months supply<br>before → after</th>
                <th class="num">Incoming 90d</th>
                <th class="num">Price</th>
                <th>Contract End</th>
                <th>Flavor</th>
                <th>Approver</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (c of Candidates; track c.ReserveOrderID + '-' + c.InventoryID) {
              <tr [class.selected-row]="isSelected(c)">
                <td>
                  <span class="band {{ bandClass(c.MatchScore) }}">{{ c.MatchScore }}</span>
                  <div class="muted small">{{ c.MatchType }}</div>
                </td>
                <td>
                  {{ c.SourceCustomer }}
                  <div class="muted small">Reserve {{ c.ReserveOrderID }} · Lot {{ c.InventoryID }}</div>
                </td>
                <td>{{ c.InventoryTypeName }}</td>
                <td>
                  {{ c.WarehouseName }}
                  @if (!c.IsSameWarehouse) { <div class="warn small">other whse</div> }
                </td>
                <td class="num">
                  {{ c.AvailableToSwap }}
                  @if (c.PendingBags > 0) { <div class="muted small">{{ c.PendingBags }} on other swaps</div> }
                </td>
                <td class="num">{{ c.MonthlyBurn > 0 ? (c.MonthlyBurn | number:'1.0-1') : '—' }}</td>
                <td class="num">
                  @if (c.MonthsSupplyBefore !== null) {
                  {{ c.MonthsSupplyBefore | number:'1.0-1' }} → {{ c.MonthsSupplyAfter | number:'1.0-1' }}
                  } @else {
                  <span class="muted small">no recent usage</span>
                  }
                </td>
                <td class="num">
                  {{ c.IncomingBags90Days ?? 0 }}
                  @if (c.NextArrival) { <div class="muted small">next {{ c.NextArrival | date:'MM/dd':'UTC' }}</div> }
                </td>
                <td class="num">
                  {{ c.Price | number:'1.2-4' }}
                  <div class="muted small">{{ c.PriceStatus }}</div>
                </td>
                <td>{{ c.ContractEndDate | date:'MM/dd/yyyy':'UTC' }}</td>
                <td>
                  @if (c.MatchedFlavorNotes) {
                  <span title="{{ c.CandidateFlavorNotes }}">{{ c.MatchedFlavorNotes }}</span>
                  } @else {
                  <span class="muted small" title="{{ c.CandidateFlavorNotes ?? '' }}">{{ c.FlavorMatchPct }}%</span>
                  }
                </td>
                <td>{{ c.ApprovingTraderName ?? '—' }}</td>
                <td>
                  @if (canEdit) {
                  <button [disabled]="isSelected(c)" (click)="selectCandidate(c)">
                    {{ isSelected(c) ? 'Selected' : 'Select' }}
                  </button>
                  }
                </td>
              </tr>
              }
            </tbody>
          </table>
        </div>
        }
      </div>
      }

    </ng-template>
  </rms-tab>

  <!-- ================================================================
       TAB 1 — Approval (6.2.18–6.2.23, 6.2.36–6.2.38)
       ================================================================ -->
  <rms-tab label="Approval">
    <ng-template>
      @if (isNew || statusId === 1) {
      <p class="muted">
        {{ isRelease ? 'Select the reserve line and submit' : 'Select a source and submit the request' }}
        — it will go to the trader who owns that reserve.
      </p>
      } @else {
      <div class="section">
        <h4>Request for Approval</h4>
        <div class="source-grid">
          <div><span class="muted">Approving trader</span><strong>{{ Swap?.ApprovingTraderName ?? '—' }}</strong></div>
          <div><span class="muted">Submitted</span><strong>{{ Swap?.SubmittedDate | date:'MM/dd/yyyy h:mm a':'UTC' }}</strong></div>
          <div><span class="muted">Proposed bags</span><strong>{{ Swap?.ProposedBags }}</strong></div>
          <div><span class="muted">Reserve price</span><strong>{{ Swap?.SourcePrice | number:'1.2-4' }} ({{ Swap?.SourcePriceStatus }})</strong></div>
          @if (Swap?.ApprovedBags) {
          <div><span class="muted">Approved bags</span><strong>{{ Swap.ApprovedBags }}</strong></div>
          <div><span class="muted">Approved price</span><strong>{{ Swap?.ApprovedPrice | number:'1.2-4' }}</strong></div>
          <div><span class="muted">Decided by</span><strong>{{ Swap?.DecisionByName ?? '—' }}</strong></div>
          }
        </div>
        @if (Swap?.Reason) {
        <p class="reason"><span class="muted">Reason:</span> {{ Swap.Reason }}</p>
        }
      </div>

      <!-- Decision form — only for the approving trader / admins while Pending -->
      @if (canDecide) {
      <div class="section decision-box">
        <h4>Your Decision</h4>
        <div class="field-grid">
          <div class="field-group">
            <label>Revised quantity (for "Approve Revised")</label>
            <input type="number" min="1" [(ngModel)]="RevisedBags" />
          </div>
          <div class="field-group">
            <label>Price (optional — can only go up)</label>
            <input type="number" step="0.01" [(ngModel)]="ApprovedPrice"
                   [placeholder]="'Keep ' + (Swap?.SourcePrice ?? '')" />
          </div>
          <div class="field-group span-4">
            <label>Comments (required)</label>
            <textarea [(ngModel)]="DecisionComments" maxlength="1000"></textarea>
          </div>
        </div>
        <div class="button-row">
          <button class="btn-bg-green" [disabled]="Busy" (click)="decide('Approve')">Approve {{ Swap?.ProposedBags }} bags</button>
          <button class="btn-bg-blue" [disabled]="Busy" (click)="decide('ApproveRevised')">Approve Revised Qty</button>
          <button class="btn-bg-yellow" [disabled]="Busy" (click)="decide('Return')">Return for Changes</button>
          <button class="btn-bg-red" [disabled]="Busy" (click)="decide('Reject')">Reject</button>
        </div>
      </div>
      } @else if (statusId === 2) {
      <p class="muted">Waiting on {{ Swap?.ApprovingTraderName ?? 'the approving trader' }}.</p>
      }

      <!-- Approval history (6.2.23) -->
      <div class="section">
        <h4>Approval History</h4>
        @if (approvalHistory.length === 0) {
        <p class="muted">No approval activity yet.</p>
        }
        @for (h of approvalHistory; track h.SwapActionHistoryID) {
        <div class="history-entry">
          <span class="history-action">{{ h.ActionType }}</span>
          <span class="history-detail">{{ h.ActionDetail }}</span>
          <span class="history-by">{{ h.ActionByName }}</span>
          <span class="history-date">{{ h.ActionDate | date:'MM/dd/yyyy h:mm a':'UTC' }}</span>
        </div>
        }
      </div>
      }
    </ng-template>
  </rms-tab>

  <!-- ================================================================
       TAB 2 — Complete Swap (6.2.24–6.2.35, 6.2.41–6.2.45)
       ================================================================ -->
  <rms-tab label="Complete Swap">
    <ng-template>
      @if (statusId === 6) {
      <div class="section completed-box">
        <h4>{{ isRelease ? 'Release Completed' : 'Swap Completed' }}</h4>
        @switch (SwapTypeID) {
        @case (1) {
        <p>
          {{ Swap?.ApprovedBags }} bags moved from {{ Swap?.SourceCustomer }}'s reserve {{ Swap?.SourceReserveOrderID }}
          to {{ Swap?.RequestingCustomer }}'s
          {{ Swap?.TargetReserveCreated ? 'new' : 'existing' }} reserve <strong>{{ Swap?.TargetReserveOrderID }}</strong>
          on {{ Swap?.ExecutedDate | date:'MM/dd/yyyy h:mm a':'UTC' }} by {{ Swap?.ExecutedByName ?? '—' }}.
        </p>
        <p class="muted">
          {{
 Swap?.TargetReserveCreated
             ? 'The new reserve copies the source price, fixation terms and contract dates.'
             : 'Bags were added to the matching line (same lot, same price).'
          }}
        </p>
        }
        @case (3) {
        <p>
          {{ Swap?.ApprovedBags }} bags released from {{ Swap?.SourceCustomer }}'s reserve {{ Swap?.SourceReserveOrderID }}
          back to general population
          on {{ Swap?.ExecutedDate | date:'MM/dd/yyyy h:mm a':'UTC' }} by {{ Swap?.ExecutedByName ?? '—' }}.
        </p>
        <p class="muted">The bags are now unreserved stock and available to sell.</p>
        }
        @default {
        <p>
          {{ Swap?.ApprovedBags }} bags moved from {{ Swap?.SourceCustomer }}'s reserve {{ Swap?.SourceReserveOrderID }}
          to Draft order <strong>#{{ Swap?.CreatedOrderID }}</strong> for {{ Swap?.RequestingCustomer }}
          on {{ Swap?.ExecutedDate | date:'MM/dd/yyyy h:mm a':'UTC' }} by {{ Swap?.ExecutedByName ?? '—' }}.
        </p>
        <p class="muted">Open the order to add pickup date, trucker and payment, then submit it as usual.</p>
        <button class="btn-bg-green" (click)="openDraftOrder()">Open Draft Order #{{ Swap?.CreatedOrderID }}</button>
        }
        }
      </div>
      } @else if (!Preview) {
      <p class="muted">The impact preview shows once a source reserve is selected.</p>
      } @else {

      <!-- Before / After -->
      <div class="impact-grid">
        <div class="impact-card">
          <h5>Source reserve line</h5>
          <div class="before-after">
            <span>{{ Preview.ReserveLineBagsBefore }}</span><span class="arrow">→</span><strong>{{ Preview.ReserveLineBagsAfter }}</strong>
          </div>
          <div class="muted small">{{ Preview.SourceCustomer }} · Reserve {{ Preview.SourceReserveOrderID }}</div>
        </div>
        <div class="impact-card">
          <h5>Source customer total ({{ Preview.Coffee }})</h5>
          <div class="before-after">
            <span>{{ Preview.SourceCustomerBagsBefore }}</span><span class="arrow">→</span><strong>{{ Preview.SourceCustomerBagsAfter }}</strong>
          </div>
          <div class="muted small">
            @if (Preview.SourceMonthsSupplyBefore !== null) {
            {{ Preview.SourceMonthsSupplyBefore | number:'1.0-1' }} → {{ Preview.SourceMonthsSupplyAfter | number:'1.0-1' }} months of supply
            (burn {{ Preview.SourceMonthlyBurn | number:'1.0-1' }}/mo)
            } @else {
            No recent usage on record
            }
          </div>
        </div>
        <div class="impact-card">
          <h5>{{ receivingCardTitle }}</h5>
          @switch (SwapTypeID) {
          @case (1) {
          <div class="before-after">
            <span>{{ Preview.RequesterReservedBagsOfThisCoffee }}</span><span class="arrow">→</span><strong>{{ Preview.RequesterReservedBagsAfter }}</strong>
          </div>
          <div class="muted small">{{ Preview.RequestingCustomer }} · {{ Preview.BagsToOrder }} bags &#64; {{ Preview.OrderLinePrice | number:'1.2-4' }}</div>
          }
          @case (3) {
          <div class="before-after"><strong>+{{ Preview.BagsToOrder }} bags</strong></div>
          <div class="muted small">Back to general population (unreserved)</div>
          }
          @default {
          <div class="before-after"><strong>{{ Preview.BagsToOrder }} bags &#64; {{ Preview.OrderLinePrice | number:'1.2-4' }}</strong></div>
          <div class="muted small">{{ Preview.RequestingCustomer }} · {{ Preview.PriceStatus }}</div>
          }
          }
        </div>
        <div class="impact-card">
          <h5>Lot holder</h5>
          <div class="before-after">
            <span>{{ Preview.LotHolderBefore }}</span><span class="arrow">→</span><strong>{{ Preview.LotHolderAfter }}</strong>
          </div>
          <div class="muted small">{{ Preview.HoldTypeBefore }} → {{ Preview.HoldTypeAfter }}</div>
        </div>
      </div>

      <div class="info-list">
        @if (Preview.TargetDescription) {
        <div><span class="muted">Result:</span>&nbsp;<strong>{{ Preview.TargetDescription }}</strong></div>
        }
        <div>
          <span class="muted">Contract:</span> {{ Preview.ContractImpact }}
          ({{ Preview.ContractStartDate | date:'MM/dd/yyyy':'UTC' }} – {{ Preview.ContractEndDate | date:'MM/dd/yyyy':'UTC' }})
        </div>
        <div>
          <span class="muted">Warehouse:</span>
          @if (isRelease) {
          {{ Preview.SourceWarehouse }} — {{ Preview.CrossWarehouseNote }}
          } @else {
          {{ Preview.SourceWarehouse }} → {{ Preview.DestinationWarehouse ?? 'Any' }} — {{ Preview.CrossWarehouseNote }}
          }
        </div>
        <div><span class="muted">Outbound:</span> {{ Preview.OutboundImpact }}</div>
      </div>

      <!-- Pre-execution checks (6.2.29–6.2.30) -->
      <div class="section">
        <h4>Checks</h4>
        <ul class="checklist">
          <li [class.ok]="Preview.StatusReady"><span>{{ Preview.StatusReady ? '✔' : '✖' }}</span> Swap is approved</li>
          <li [class.ok]="Preview.InventoryValid"><span>{{ Preview.InventoryValid ? '✔' : '✖' }}</span> Inventory lot exists</li>
          <li [class.ok]="Preview.ReserveValid"><span>{{ Preview.ReserveValid ? '✔' : '✖' }}</span> Source reserve is valid</li>
          <li [class.ok]="Preview.ContractValid"><span>{{ Preview.ContractValid ? '✔' : '✖' }}</span> Contract not expired</li>
          <li [class.ok]="Preview.QuantityValid"><span>{{ Preview.QuantityValid ? '✔' : '✖' }}</span> Enough bags on the reserve line</li>
          <li [class.ok]="Preview.PricingValid"><span>{{ Preview.PricingValid ? '✔' : '✖' }}</span> Price not lowered</li>
          <li [class.ok]="Preview.CrossWarehouseValid"><span>{{ Preview.CrossWarehouseValid ? '✔' : '✖' }}</span> Warehouse allowed</li>
        </ul>
      </div>

      <div class="button-row">
        <button class="btn-bg-blue" [disabled]="Busy" (click)="refreshPreview()">Refresh</button>
        <button class="btn-bg-green" [disabled]="Busy || !canExecute" (click)="executeSwap()">
          {{ executeLabel }}
        </button>
        @if (statusId !== 4) {
        <span class="muted small">Available once the swap is approved.</span>
        }
      </div>
      }
    </ng-template>
  </rms-tab>

  <!-- ================================================================
       TAB 3 — Notes (6.2.46)
       ================================================================ -->
  <rms-tab label="Notes ({{ Notes.length }})">
    <ng-template>
      @if (isNew) {
      <p class="muted">Save the request first, then notes can be added.</p>
      } @else {
      <div class="section">
        <h4>{{ NoteForm.SwapNoteID ? 'Edit Note' : 'Add Note' }}</h4>
        <div class="field-grid">
          <div class="field-group">
            <label>Type</label>
            <select [(ngModel)]="NoteForm.NoteType">
              @for (t of NoteTypes; track t) {
              <option [value]="t">{{ t }}</option> }
            </select>
          </div>
          <div class="field-group">
            <label>Related Step</label>
            <select [(ngModel)]="NoteForm.RelatedStep">
              <option [ngValue]="null">—</option>
              @for (s of RelatedSteps; track s) {
              <option [ngValue]="s">{{ s }}</option> }
            </select>
          </div>
          <div class="field-group checkbox-col">
            <label><input type="checkbox" [(ngModel)]="NoteForm.IsInternal" /> Internal only</label>
            <label><input type="checkbox" [(ngModel)]="NoteForm.FollowUpRequired" /> Follow-up required</label>
          </div>
          <div class="field-group">
            @if (NoteForm.FollowUpRequired) {
            <rms-date label="Follow-up Date" [(ngModel)]="NoteForm.FollowUpDate"></rms-date>
            }
          </div>
          <div class="field-group span-4">
            <label>Note ({{ NoteForm.NoteText.length }}/2000)</label>
            <textarea [(ngModel)]="NoteForm.NoteText" maxlength="2000"></textarea>
          </div>
        </div>
        <div class="button-row">
          @if (NoteForm.SwapNoteID) {
          <button class="btn-bg-red" (click)="resetNote()">Cancel Edit</button>
          }
          <button class="btn-bg-green" [disabled]="Busy" (click)="saveNote()">Save Note</button>
        </div>
      </div>

      <div class="section">
        @if (Notes.length === 0) {
        <p class="muted">No notes yet.</p>
        }
        @for (n of Notes; track n.SwapNoteID) {
        <div class="note-card">
          <div class="note-head">
            <span class="note-type">{{ n.NoteType }}</span>
            @if (n.RelatedStep) { <span class="chip">{{ n.RelatedStep }}</span> }
            @if (n.IsInternal) { <span class="chip">Internal</span> }
            @if (n.FollowUpRequired) { <span class="chip chip-warn">Follow up {{ n.FollowUpDate | date:'MM/dd/yyyy':'UTC' }}</span> }
            <span class="spacer"></span>
            <span class="muted small">
              {{ n.CreatedByName ?? '—' }} · {{ n.CreatedDate | date:'MM/dd/yyyy h:mm a':'UTC' }}
              @if (n.UpdatedDate) { (edited {{ n.UpdatedDate | date:'MM/dd h:mm a':'UTC' }}) }
            </span>
            <button class="link-btn" (click)="editNote(n)">Edit</button>
          </div>
          <div class="note-text">{{ n.NoteText }}</div>
        </div>
        }
      </div>
      }
    </ng-template>
  </rms-tab>

  <!-- ================================================================
       TAB 4 — Audit (6.2.39, 6.2.47, 6.2.48)
       ================================================================ -->
  <rms-tab label="Audit">
    <ng-template>
      @if (isNew) {
      <p class="muted">No history yet.</p>
      } @else {
      <div class="button-row audit-actions">
        <span class="muted small">Audit entries cannot be edited or deleted.</span>
        <button class="btn-bg-blue" (click)="exportAudit()">Export CSV</button>
      </div>
      <table class="doc-table">
        <thead>
          <tr><th>Date</th><th>Action</th><th>Detail</th><th>Ref</th><th class="num">Bags</th><th>Before → After</th><th>By</th></tr>
        </thead>
        <tbody>
          @for (h of History; track h.SwapActionHistoryID) {
          <tr>
            <td class="nowrap">{{ h.ActionDate | date:'MM/dd/yyyy h:mm a':'UTC' }}</td>
            <td class="nowrap"><strong>{{ h.ActionType }}</strong></td>
            <td>{{ h.ActionDetail }}</td>
            <td class="nowrap">{{ h.ReferenceID }}</td>
            <td class="num">{{ h.QuantityBags }}</td>
            <td class="nowrap">{{ h.BeforeValue !== null || h.AfterValue !== null ? (h.BeforeValue ?? '—') + ' → ' + (h.AfterValue ?? '—') : '' }}</td>
            <td class="nowrap">{{ h.ActionByName }}</td>
          </tr>
          }
        </tbody>
      </table>
      }
    </ng-template>
  </rms-tab>

</rms-tab-group>
}
